import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/lib/utils'
import { useAuthModal } from '@/features/auth'
import { LANDING_VIDEOS } from '@/config/assets'

/**
 * Hero section with headline, CTAs and video
 * Implementation: LAND2-001
 */
export function HeroSection() {
  const { t } = useTranslation()
  const { openAuthModal } = useAuthModal()

  return (
    <div className="relative overflow-hidden bg-white">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8 lg:py-32">
        <div className="flex flex-col items-center gap-12 lg:flex-row lg:items-center lg:justify-between lg:gap-16">
          {/* Text Content */}
          <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
            {/* Headline */}
            <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              <span className="block">{t('landing.hero.headline')}</span>
              <span className="mt-2 block text-accent">{t('landing.hero.subheadline')}</span>
            </h1>

            {/* Description */}
            <p className="mt-6 max-w-xl text-lg text-foreground-muted sm:text-xl">
              {t('landing.hero.description')}
            </p>

            {/* CTAs */}
            <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:gap-4">
              {/* Primary CTA */}
              <button
                onClick={() => openAuthModal()}
                className={cn(
                  'rounded-lg bg-accent px-8 py-4 text-lg font-semibold text-white',
                  'transition-all duration-200 ease-out',
                  'hover:-translate-y-0.5 hover:bg-accent-hover hover:shadow-lg hover:shadow-accent/30',
                  'focus:outline-none focus:ring-2 focus:ring-accent/50 focus:ring-offset-2'
                )}
              >
                {t('landing.hero.cta.primary')}
              </button>

              {/* Secondary CTA */}
              <button
                onClick={() => openAuthModal()}
                className={cn(
                  'rounded-lg border border-border bg-white px-8 py-4 text-lg font-semibold text-foreground',
                  'transition-all duration-200',
                  'hover:bg-background-secondary',
                  'focus:outline-none focus:ring-2 focus:ring-accent/50 focus:ring-offset-2'
                )}
              >
                {t('landing.hero.cta.secondary')}
              </button>
            </div>
          </div>

          {/* Video/Visual */}
          <div className="relative w-full max-w-lg lg:max-w-xl">
            <div className="relative aspect-[5/4] w-full overflow-hidden rounded-2xl bg-background-secondary shadow-2xl">
              <video
                autoPlay
                loop
                muted
                playsInline
                className="h-full w-full object-cover"
                poster="/images/hero-poster.png"
              >
                <source src={LANDING_VIDEOS.hero} type="video/webm" />
                {/* Fallback for browsers that don't support WebM */}
                <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-accent/10 to-primary/10">
                  <span className="text-foreground-muted">Video not supported</span>
                </div>
              </video>

              {/* Decorative gradient overlay */}
              <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-black/10" />
            </div>

            {/* Decorative elements */}
            <div className="absolute -right-4 -top-4 -z-10 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />
            <div className="absolute -bottom-8 -left-8 -z-10 h-72 w-72 rounded-full bg-primary/5 blur-3xl" />
          </div>
        </div>
      </div>
    </div>
  )
}
